import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sparkle, ArrowRight, ArrowLeft, CheckCircle, XCircle, Cloud, HardDrives,
  Play, ArrowClockwise, Info, Warning, Rocket, X,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_META = {
  shizai: { label: '資材', hint: '受入写真・請求書・PO 添付' },
  mfg: { label: '製造', hint: '図面・工程写真・添付ファイル' },
  knowledge: { label: 'ナレッジ', hint: 'ドキュメント・画像' },
  it: { label: 'IT', hint: '内部資料' },
};

const STRATEGIES = [
  {
    key: 'new_only',
    label: '新規アップロードのみ切り替え',
    accent: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    icon: ArrowRight,
    pros: '最速・最安全。既存ファイルは Emergent に残ります',
    cons: 'NAS 上には過去データがないため、Emergent との併存になります',
    copy: false, delete: false,
  },
  {
    key: 'copy',
    label: '既存ファイルもコピー',
    recommended: true,
    accent: 'text-blue-700 bg-blue-50 border-blue-200',
    icon: Play,
    pros: '両方の場所にコピーが残るため、ロールバック可能。SHA-256 で検証',
    cons: '一時的に Emergent・NAS の両方で容量を消費します',
    copy: true, delete: false,
  },
  {
    key: 'move',
    label: '既存ファイルを移動（コピー＋Emergent 側を削除）',
    accent: 'text-rose-700 bg-rose-50 border-rose-200',
    icon: Rocket,
    pros: 'Emergent 側の容量が空くため、実質完全移行',
    cons: '削除後は Emergent から復元できません（NAS 上に残るのみ）',
    copy: true, delete: true,
  },
];

const DISMISS_KEY = 'sensbey.nas_wizard_dismissed_v1';

/**
 * NAS Upload Wizard — first-time onboarding for admins moving an app from
 * Emergent Cloud to their on-prem NAS.
 *
 * Flow:
 *  1. Prerequisites (NAS reachable? at least one app still on Emergent?)
 *  2. Pick an app
 *  3. Pick strategy (new-only / copy / move)
 *  4. Execute (PUT toggle → optional POST migration) + surface result
 *  5. Success (with tips for next steps)
 *
 * The trigger card only appears when both:
 *   - NAS is configured (`config.nas_configured === true`)
 *   - AND at least one app is still routed to Emergent
 *   - AND the user hasn't dismissed it
 * Otherwise the card stays hidden so it doesn't nag experienced admins.
 */
export function NasWizardCard({ config, onFinished }) {
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedApp, setSelectedApp] = useState(null);
  const [strategyKey, setStrategyKey] = useState('copy');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {migrationId?, warning?, error?}

  useEffect(() => {
    try { setDismissed(localStorage.getItem(DISMISS_KEY) === '1'); } catch (_) { /* ignore */ }
  }, []);

  // Compute eligible apps: those still on Emergent (with ANY file count so
  // there's something to potentially copy — but we don't hard-block empty
  // apps since users may want to toggle them proactively).
  const eligibleApps = Object.entries(config?.apps || {})
    .filter(([, backend]) => backend === 'emergent')
    .map(([key]) => key);

  const shouldShowTrigger =
    !dismissed && (config?.nas_configured === true) && eligibleApps.length > 0;

  if (!shouldShowTrigger && !open) return null;

  const dismissForever = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  const startWizard = () => {
    setOpen(true);
    setStep(1);
    setSelectedApp(null);
    setStrategyKey('copy');
    setResult(null);
  };

  const closeWizard = () => {
    setOpen(false);
    setStep(1);
    setResult(null);
  };

  const strategy = STRATEGIES.find((s) => s.key === strategyKey);
  const fileCount = selectedApp ? (config?.file_counts?.[selectedApp] ?? 0) : 0;

  const executeWizard = async () => {
    if (!selectedApp || !strategy) return;
    setSubmitting(true);
    setResult(null);
    try {
      // 1. Flip routing
      await axios.put(`${API}/settings/storage`, { app: selectedApp, backend: 'nas' });
      // 2. Optional copy / move
      let migrationId = null;
      if (strategy.copy && fileCount > 0) {
        const r = await axios.post(`${API}/settings/storage/migrations`, {
          app: selectedApp, source: 'emergent', target: 'nas', delete_source: strategy.delete,
        });
        migrationId = r.data?.id || null;
      }
      setResult({ migrationId, warning: (strategy.copy && fileCount === 0) ? '既存ファイルは 0 件でした（コピー処理はスキップ）' : null });
      setStep(5);
      if (onFinished) onFinished();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'ウィザード実行に失敗しました' });
      toast.error(e.response?.data?.detail || 'ウィザード実行に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Trigger card */}
      {shouldShowTrigger && !open && (
        <div
          className="relative rounded-xl border-2 border-dashed border-indigo-300 bg-zinc-50 p-5 shadow-sm"
          data-testid="nas-wizard-trigger"
        >
          <button
            onClick={dismissForever}
            className="absolute top-2 right-2 text-zinc-400 hover:text-zinc-700 p-1 rounded"
            title="今後表示しない"
            data-testid="nas-wizard-dismiss"
          >
            <X size={14} />
          </button>
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-700 text-white shrink-0 shadow-lg">
              <Sparkle size={22} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-zinc-900 text-[15px] flex items-center gap-2">
                NAS 切り替えウィザード
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-600 text-white">おすすめ</span>
              </h3>
              <p className="text-[12px] text-zinc-600 mt-1 leading-relaxed">
                NAS が正常に接続されています。<b>{eligibleApps.length}</b> 個のアプリがまだ Emergent 上にあります。
                3 ステップで安全に切り替えられるガイドを開始しましょう。
              </p>
              <div className="mt-3 flex items-center gap-2">
                <Button
                  onClick={startWizard}
                  data-testid="nas-wizard-start"
                  className="bg-teal-700 hover:bg-teal-800 text-white shadow-sm"
                >
                  <Sparkle size={14} weight="duotone" className="mr-1" />
                  ウィザードを開始
                </Button>
                <button
                  onClick={dismissForever}
                  className="text-[11px] text-zinc-500 hover:text-zinc-800 underline"
                  data-testid="nas-wizard-dismiss-text"
                >
                  今後表示しない
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wizard dialog */}
      <Dialog open={open} onOpenChange={(o) => !o && closeWizard()}>
        <DialogContent
          className="max-w-2xl"
          data-testid="nas-wizard-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} weight="duotone" className="text-indigo-600" />
              NAS 切り替えウィザード
              <span className="ml-auto text-[11px] text-zinc-400 font-normal">
                ステップ {Math.min(step, 4)} / 4
              </span>
            </DialogTitle>
            <DialogDescription>
              アプリの保存先を Emergent Cloud から NAS へ切り替える 4 ステップのガイドです。
            </DialogDescription>
          </DialogHeader>

          {/* Progress bar */}
          <div className="flex items-center gap-1.5 mb-2" data-testid="nas-wizard-progress">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  step > n ? 'bg-emerald-500' : step === n ? 'bg-indigo-500' : 'bg-zinc-200'
                }`}
              />
            ))}
          </div>

          {/* ── Step 1: Welcome + prerequisites ─────────────────────── */}
          {step === 1 && (
            <div className="space-y-4" data-testid="nas-wizard-step-1">
              <div>
                <h4 className="font-bold text-zinc-900 mb-1">はじめに</h4>
                <p className="text-[13px] text-zinc-600 leading-relaxed">
                  このウィザードでは、いずれかのアプリの保存先を
                  <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[11px]">
                    <Cloud size={11} weight="duotone" />Emergent
                  </span>
                  から
                  <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[11px]">
                    <HardDrives size={11} weight="duotone" />NAS
                  </span>
                  に切り替えます。
                </p>
              </div>

              <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-200">
                <div className="px-4 py-3 flex items-start gap-3 text-[13px]">
                  <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-zinc-900">NAS 接続 OK</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {config?.nas_url ? (
                        <>接続先: <span className="font-mono">{config.nas_url}</span></>
                      ) : '設定済み'}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 flex items-start gap-3 text-[13px]">
                  <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-zinc-900">切り替え対象 {eligibleApps.length} アプリ</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {eligibleApps.map((k) => APP_META[k]?.label || k).join('、')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 flex items-start gap-2 text-[12px] text-blue-900">
                <Info size={14} weight="duotone" className="text-blue-600 shrink-0 mt-0.5" />
                <span>
                  すべての操作はいつでも元に戻せます（安全策として、削除を選ばない限り Emergent 側のファイルは残ります）。
                </span>
              </div>
            </div>
          )}

          {/* ── Step 2: Pick an app ─────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-3" data-testid="nas-wizard-step-2">
              <div>
                <h4 className="font-bold text-zinc-900 mb-1">切り替えるアプリを選択</h4>
                <p className="text-[12px] text-zinc-500">
                  複数のアプリを一度に切り替えるのは推奨しません。最初は 1 つで動作確認しましょう。
                </p>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {eligibleApps.map((key) => {
                  const meta = APP_META[key] || { label: key, hint: '' };
                  const count = config?.file_counts?.[key] ?? 0;
                  const active = selectedApp === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedApp(key)}
                      data-testid={`nas-wizard-app-${key}`}
                      className={`relative flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 shadow-md'
                          : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        active ? 'bg-indigo-600 text-white' : 'bg-zinc-100 text-zinc-500'
                      }`}>
                        <HardDrives size={16} weight="duotone" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-zinc-900">{meta.label}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{meta.hint}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[13px] font-bold tabular-nums text-zinc-900">{count}</div>
                        <div className="text-[10px] text-zinc-500">既存ファイル</div>
                      </div>
                      {active && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                          <CheckCircle size={12} weight="fill" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 3: Pick strategy ───────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-3" data-testid="nas-wizard-step-3">
              <div>
                <h4 className="font-bold text-zinc-900 mb-1">既存ファイルの扱いを選択</h4>
                <p className="text-[12px] text-zinc-500">
                  <b>{APP_META[selectedApp]?.label}</b> には既存ファイルが <b>{fileCount.toLocaleString()}</b> 件あります。
                </p>
              </div>
              <div className="space-y-2">
                {STRATEGIES.map((s) => {
                  const active = strategyKey === s.key;
                  // Move option only makes sense when files exist
                  const disabled = s.copy && fileCount === 0;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => !disabled && setStrategyKey(s.key)}
                      disabled={disabled}
                      data-testid={`nas-wizard-strategy-${s.key}`}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                        disabled
                          ? 'opacity-40 cursor-not-allowed border-zinc-200'
                          : active
                            ? `border-indigo-500 bg-indigo-50 shadow-md`
                            : `border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50`
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 shrink-0 mt-0.5 ${
                          active ? 'border-indigo-600 bg-indigo-600' : 'border-zinc-300'
                        } flex items-center justify-center`}>
                          {active && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-zinc-900 flex items-center gap-1.5">
                            <s.icon size={13} weight="duotone" className={active ? 'text-indigo-600' : 'text-zinc-500'} />
                            {s.label}
                            {s.recommended && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                おすすめ
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-zinc-600 mt-1 space-y-0.5">
                            <div className="flex items-start gap-1">
                              <span className="text-emerald-600 shrink-0">利点</span>
                              <span>· {s.pros}</span>
                            </div>
                            <div className="flex items-start gap-1">
                              <span className="text-amber-600 shrink-0">注意</span>
                              <span>· {s.cons}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {strategyKey === 'move' && (
                <div className="rounded-md bg-rose-50 border border-rose-300 px-3 py-2 flex items-start gap-2 text-[12px] text-rose-900">
                  <Warning size={14} weight="duotone" className="text-rose-600 shrink-0 mt-0.5" />
                  <span>
                    移動を選択した場合、コピー・検証・DB 更新が <b>すべて成功したファイル</b> のみ Emergent から削除されます。ロールバックは不可です。
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Confirm & execute ───────────────────────────── */}
          {step === 4 && (
            <div className="space-y-4" data-testid="nas-wizard-step-4">
              <div>
                <h4 className="font-bold text-zinc-900 mb-1">実行内容の確認</h4>
                <p className="text-[12px] text-zinc-500">下記の操作を実行します。よろしければ「実行」を押してください。</p>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 divide-y divide-zinc-200">
                <div className="px-4 py-3 flex items-center gap-3 text-[13px]">
                  <span className="font-semibold text-zinc-700 w-24 shrink-0">対象アプリ</span>
                  <span className="text-zinc-900 font-bold">{APP_META[selectedApp]?.label}</span>
                </div>
                <div className="px-4 py-3 flex items-center gap-3 text-[13px]">
                  <span className="font-semibold text-zinc-700 w-24 shrink-0">切り替え</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Cloud size={13} weight="duotone" className="text-sky-600" /> Emergent
                    <ArrowRight size={11} className="text-zinc-400" />
                    <HardDrives size={13} weight="duotone" className="text-amber-600" /> NAS
                  </span>
                </div>
                <div className="px-4 py-3 flex items-start gap-3 text-[13px]">
                  <span className="font-semibold text-zinc-700 w-24 shrink-0">既存ファイル</span>
                  <span className="text-zinc-900">
                    {strategy.copy && fileCount > 0 ? (
                      <>
                        {fileCount.toLocaleString()} 件を <b>NAS へコピー</b>
                        {strategy.delete && <span className="text-rose-600 font-bold"> ＋ Emergent 側を削除</span>}
                      </>
                    ) : fileCount === 0 ? (
                      <span className="text-zinc-500">既存ファイル無し（コピー処理なし）</span>
                    ) : (
                      <span className="text-zinc-500">Emergent 上にそのまま維持（コピーなし）</span>
                    )}
                  </span>
                </div>
              </div>
              {result?.error && (
                <div className="rounded-md bg-rose-50 border border-rose-300 px-3 py-2 text-[12px] text-rose-900">
                  <b>エラー:</b> {result.error}
                </div>
              )}
            </div>
          )}

          {/* ── Step 5: Success ─────────────────────────────────────── */}
          {step === 5 && (
            <div className="space-y-4 py-2" data-testid="nas-wizard-step-5">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-zinc-50 text-emerald-700 mb-3">
                  <CheckCircle size={32} weight="duotone" />
                </div>
                <h4 className="font-bold text-zinc-900 text-lg">切り替え完了！</h4>
                <p className="text-[13px] text-zinc-600 mt-1">
                  <b>{APP_META[selectedApp]?.label}</b> の新規アップロード先が NAS になりました。
                </p>
              </div>
              {result?.warning && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900">
                  {result.warning}
                </div>
              )}
              {result?.migrationId && (
                <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-[12px] text-blue-900">
                  <b>コピー処理を開始しました。</b> 「移行履歴」セクションで進捗を確認できます（ID: <span className="font-mono">{result.migrationId.slice(0, 8)}…</span>）。
                </div>
              )}
              <div className="rounded-lg border border-zinc-200 p-3 text-[12px] space-y-2">
                <div className="font-semibold text-zinc-900">次のステップ:</div>
                <ul className="list-disc pl-5 space-y-1 text-zinc-700">
                  <li><b>{APP_META[selectedApp]?.label}</b> で新しいファイルをアップロードしてみましょう。NAS のディスクに直接書き込まれます。</li>
                  <li>問題があれば、上部のセグメントスイッチで <b>Emergent に戻す</b> ことができます。</li>
                  <li>他のアプリも同じ手順で切り替えられます。</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter className="flex-row justify-between sm:justify-between">
            <div>
              {step > 1 && step < 5 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="nas-wizard-back">
                  <ArrowLeft size={13} className="mr-1" />戻る
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {step < 4 && (
                <>
                  <Button variant="outline" onClick={closeWizard} data-testid="nas-wizard-cancel-early">
                    キャンセル
                  </Button>
                  <Button
                    onClick={() => setStep(step + 1)}
                    disabled={step === 2 && !selectedApp}
                    data-testid="nas-wizard-next"
                    className="bg-teal-700 hover:bg-teal-800 text-white"
                  >
                    次へ<ArrowRight size={13} className="ml-1" />
                  </Button>
                </>
              )}
              {step === 4 && (
                <>
                  <Button variant="outline" onClick={closeWizard} disabled={submitting} data-testid="nas-wizard-cancel-final">
                    キャンセル
                  </Button>
                  <Button
                    onClick={executeWizard}
                    disabled={submitting}
                    data-testid="nas-wizard-execute"
                    className={strategy?.delete
                      ? 'bg-rose-600 hover:bg-rose-700 text-white'
                      : 'bg-teal-700 hover:bg-teal-800 text-white'}
                  >
                    {submitting ? (
                      <><ArrowClockwise size={13} className="mr-1 animate-spin" />実行中…</>
                    ) : (
                      <><Rocket size={13} className="mr-1" />実行</>
                    )}
                  </Button>
                </>
              )}
              {step === 5 && (
                <Button
                  onClick={closeWizard}
                  data-testid="nas-wizard-done"
                  className="bg-teal-700 hover:bg-teal-800 text-white"
                >
                  完了<CheckCircle size={13} className="ml-1" />
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
